import React from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueries, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import type {Member} from '@/entities/member/types';
import {fetchRoleHolders, revokeRolesInCompany} from '@/entities/role/api';
import type {RoleHolder} from '@/entities/role/types';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppText} from '@/shared/ui/text';
import {spacing} from '@/shared/ui/theme';

/**
 * People who hold a role in this company but are not in its member list.
 *
 * ## The hole this closes
 *
 * Membership and authority live in two different services. `DELETE
 * /companies/{id}/members/{member_id}` takes a person off sv_organization's list; it
 * does not touch sv_permissions, where the Casbin grant lives. Remove an administrator
 * the ordinary way and their `admin` role survives — they no longer appear on the
 * members screen, so nothing offers to take it back, and they keep passing every check
 * the gateway makes. An owner auditing "who can administer this company" by reading the
 * member list gets an answer that is confidently wrong.
 *
 * `GET /roles/{role_code}/users` is the other half of the picture, and this card is the
 * difference between the two lists. Everywhere else in the cabinet that route would be
 * redundant — `/companies/{id}/members` already carries each member's `roles` — which
 * is exactly why it is worth calling here and nowhere else.
 *
 * ## Why it is careful about what it calls a ghost
 *
 * The holder rows are joined together by the gateway out of two services and the
 * account half is optional; which field carries the account id is not something this
 * app can verify from the wire. So: a row with no id it recognises is SKIPPED, never
 * reported. The failure mode of guessing wrong is "every administrator in the company
 * is flagged as a ghost", and a false alarm on this card invites exactly the wrong
 * action — an owner revoking roles from people who still have them legitimately.
 *
 * A failing query is silence, not an error banner: this is a background cross-check on
 * a screen that already has a job, and a company whose gateway is older than the route
 * should see the member list, not a complaint about it.
 *
 * ## The answer is platform-wide
 *
 * `x-company-id` on this route chooses the Casbin domain the CALLER is checked in; it
 * does not narrow the data. Ask for `owner` and the reply is every owner of every
 * company, one row per assignment, each carrying its own `company_id`. Shown as-is, the
 * card listed fifteen strangers' account ids under one company and offered to «revoke»
 * them — a leak of other tenants' ids and a false alarm on every row. So a row counts
 * only when its `company_id` is THIS company and the assignment is still active; a row
 * with no `company_id` is skipped for the same reason a row with no account id is.
 */
const styles = StyleSheet.create({
  card: {gap: spacing(3)},
  row: {flexDirection: 'row', alignItems: 'center', gap: spacing(2)},
  body: {flex: 1, gap: 2},
});

/** The account id, whichever of the two spellings the join produced. Null = unusable. */
function holderAccountId(holder: RoleHolder): string | null {
  const id = holder.user_id || holder.id;
  return id ? id.trim().toLowerCase() : null;
}

/** Does this assignment belong to the company on screen, and is it still in force? */
function holderInCompany(holder: RoleHolder, companyId: string): boolean {
  if (holder.is_active === false) {
    return false;
  }
  const scope = (holder.company_id || '').trim().toLowerCase();
  return scope !== '' && scope === companyId.trim().toLowerCase();
}

/**
 * The difference between the two lists: holders of `roleCodes[i]` (from
 * `holdersByRole[i]`) who are in this company and not among `members`. Pure, so the
 * rule that decides what is a ghost can be tested without a query client.
 */
export function collectUnlisted(
  holdersByRole: RoleHolder[][],
  members: Member[],
  companyId: string,
  roleCodes: string[],
): Map<string, {name: string; roles: string[]}> {
  const known = new Set(
    members.map(m => (m.account_id || '').trim().toLowerCase()).filter(Boolean),
  );
  const unlisted = new Map<string, {name: string; roles: string[]}>();
  holdersByRole.forEach((holders, index) => {
    for (const holder of holders) {
      // Another company's assignment, or a revoked one — not ours to report.
      if (!holderInCompany(holder, companyId)) {
        continue;
      }
      const accountId = holderAccountId(holder);
      // No id we recognise — see the module note. Skipped, never reported.
      if (!accountId || known.has(accountId)) {
        continue;
      }
      const entry = unlisted.get(accountId) ?? {name: holderName(holder), roles: []};
      entry.roles.push(roleCodes[index]);
      unlisted.set(accountId, entry);
    }
  });
  return unlisted;
}

function holderName(holder: RoleHolder): string {
  return (
    holder.full_name ||
    holder.email ||
    holder.username ||
    holder.phone ||
    holderAccountId(holder) ||
    ''
  );
}

export function UnlistedRoleHolders({
  companyId,
  members,
  roleCodes,
}: {
  companyId: string;
  members: Member[];
  /** The roles worth auditing — the ones this screen can also grant, plus `owner`. */
  roleCodes: string[];
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();

  const queries = useQueries({
    queries: roleCodes.map(code => ({
      queryKey: ['role-holders', companyId, code],
      queryFn: () => fetchRoleHolders(companyId, code, {pageSize: 100}),
      // Cheap to be wrong about for a minute; expensive to re-ask on every focus.
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });

  const revoke = useMutation({
    mutationFn: (accountId: string) => revokeRolesInCompany(companyId, accountId),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({queryKey: ['role-holders', companyId]}),
        qc.invalidateQueries({queryKey: ['members', companyId]}),
      ]);
    },
  });

  // Anything still loading, or a route this gateway does not serve: say nothing at all
  // rather than reporting a partial answer as a complete one.
  if (queries.some(q => q.isPending || q.isError)) {
    return null;
  }

  const unlisted = collectUnlisted(
    queries.map(q => q.data?.users ?? []),
    members,
    companyId,
    roleCodes,
  );

  if (unlisted.size === 0) {
    return null;
  }

  const errorOf = (e: unknown): string =>
    e instanceof ApiError ? e.message : t('errors.generic');

  return (
    <Card padding={4} style={styles.card}>
      <Banner
        tone="warning"
        title={t('members.unlisted.title')}
        message={t('members.unlisted.hint')}
      />
      {revoke.isError ? (
        <Banner tone="error" title={errorOf(revoke.error)} />
      ) : null}
      {[...unlisted.entries()].map(([accountId, entry]) => (
        <View key={accountId} style={styles.row}>
          <View style={styles.body}>
            <AppText variant="body" numberOfLines={1}>
              {entry.name}
            </AppText>
            <AppText variant="caption" numberOfLines={1}>
              {entry.roles.join(', ')}
            </AppText>
          </View>
          <AppButton
            title={t('members.unlisted.revoke')}
            tone="destructive"
            size="sm"
            loading={revoke.isPending && revoke.variables === accountId}
            onPress={() => revoke.mutate(accountId)}
            testID={`unlisted-revoke-${accountId}`}
          />
        </View>
      ))}
    </Card>
  );
}
